<script setup lang="ts">
  import { computed, onUnmounted } from 'vue';
  import { useWizardStore } from '../../store/wizard-stepper.store';
  import type { WizardFooterActionPayload } from '../WizardFooter/WizardFooter.types';
  import WizardFooter from '../WizardFooter/WizardFooter.vue';
  import WizardHeader from '../WizardHeader/WizardHeader.vue';
  import { contentClass, innerClass, wrapperClass } from './Wizard.css';
  import type { WizardProps } from './Wizard.types';

  const props = defineProps<WizardProps>();

  const emit = defineEmits<{
    next: [payload: WizardFooterActionPayload];
    back: [payload: WizardFooterActionPayload];
    submit: [payload: WizardFooterActionPayload];
    close: [payload: WizardFooterActionPayload];
  }>();

  const stepperStore = useWizardStore();

  const stepper = computed(() => stepperStore.steppers[props.namespace]);

  const activeIndex = computed(() => stepper.value?.activeIndex ?? 0);

  const currentStep = computed(() => stepper.value?.steps[activeIndex.value]);

  const currentComponent = computed(() => currentStep.value?.component);

  const sharedProps = computed(() => stepper.value?.sharedProps ?? {});

  const onNext = (payload: WizardFooterActionPayload) => {
    emit('next', payload);
  };

  const onBack = (payload: WizardFooterActionPayload) => {
    emit('back', payload);
  };

  const onSubmit = (payload: WizardFooterActionPayload) => {
    emit('submit', payload);
  };

  const onClose = (payload: WizardFooterActionPayload) => {
    emit('close', payload);
  };

  onUnmounted(() => {
    if (!stepper.value?.resumable) {
      stepperStore.destroyStepper(props.namespace);
    }
  });
</script>

<template>
  <div v-if="stepper" :class="wrapperClass" :data-testid="testId">
    <WizardHeader
      :namespace="namespace"
      :title="currentStep?.title ?? ''"
      :description="currentStep?.description"
      :test-id="testId ? `${testId}-header` : undefined"
    />
    <div :class="contentClass">
      <div :class="innerClass">
        <component
          :is="currentComponent"
          v-if="currentComponent"
          v-bind="sharedProps"
        />
      </div>
    </div>
    <WizardFooter
      :namespace="namespace"
      :test-id="testId ? `${testId}-footer` : undefined"
      @next="onNext"
      @back="onBack"
      @submit="onSubmit"
      @close="onClose"
    >
      <template v-if="$slots['footer-start']" #footer-start="slotProps">
        <slot name="footer-start" v-bind="slotProps" />
      </template>
    </WizardFooter>
  </div>
</template>
